import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  CopilotChatActivityStage,
  CopilotResponseLength,
  CopilotConversationSummary,
  CopilotInstructionProfile,
  CopilotSavedPrompt,
  CopilotSettings,
  CopilotAgent,
  CopilotUploadPolicy,
  KnowledgeScope,
} from "@yazamco/shared-types";
import { useSearchParams } from "react-router-dom";
import { NavChatSlot } from "../../components/layout/navSlot";
import { useToast } from "../../components/feedback/Toast";
import { useConfirm } from "../../components/feedback/ConfirmDialog";
import {
  CopilotApiError,
  CopilotLatencyTrace,
  sendCopilotMessage,
} from "../../lib/copilot";
import {
  downloadFile,
  exportFileName,
  toJson,
  toMarkdown,
} from "./copilot-export";
import { scopeLabel } from "./KnowledgeScopePicker";
import { useBusinessTools } from "./BusinessToolsPicker";
import { CopilotSessionList } from "./CopilotSessionList";
import { CopilotSettingsDialog } from "./CopilotSettingsDialog";
import { ConversationView } from "./ConversationView";
import { messageTime, type ChatMessage, type PendingFile } from "./copilot-chat";
import {
  useAppendCopilotTurn,
  useCopilotConversation,
  useCopilotConversations,
  useCopilotSavedPrompts,
  useCopilotSettings,
  useCopilotAgents,
  useCreateCopilotSavedPrompt,
  useDeleteCopilotConversation,
  useDeleteCopilotSavedPrompt,
  useUpdateCopilotConversation,
  useUpdateCopilotSettings,
} from "./hooks";
import "./copilot.css";

/** Where the last-open chat is remembered, so a reload lands where you were. */
const ACTIVE_KEY = "yzc.copilot.activeConversation";

/**
 * Error codes a reconnect can actually fix. `COPILOT_AUTH_REQUIRED` is a
 * missing or dead Microsoft grant; `COPILOT_CONSENT_REQUIRED` is a live grant
 * that never covered Copilot — either way the gesture is the same. A genuine
 * `COPILOT_ACCESS_DENIED` (no licence, tenant policy) is deliberately absent:
 * offering a reconnect there sends the user round a loop that cannot succeed.
 */
const RECONNECTABLE = new Set(["COPILOT_AUTH_REQUIRED", "COPILOT_CONSENT_REQUIRED"]);

function readActiveId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

/**
 * The Copilot workspace: conversation state, the turn lifecycle and the server
 * bindings. Everything visual lives in `CopilotSessionList` (the chat rail),
 * `ConversationView` (toolbar, transcript, composer) and
 * `CopilotSettingsDialog`.
 */
export function CopilotPage(): JSX.Element {
  const toast = useToast();
  const confirm = useConfirm();
  const businessTools = useBusinessTools();

  /**
   * The open chat is in the URL as `?chat=<id>`.
   *
   * That is what makes the export menu's "Copy link to this chat" a real
   * link rather than a gesture: the address bar already names the chat, so
   * the item copies what is there. localStorage stays as the fallback for a
   * plain /copilot visit, which is the "resume where I was" behaviour.
   */
  const [params, setParams] = useSearchParams();
  const [activeId, setActiveId] = useState<string | null>(
    () => params.get("agent") ? null : params.get("chat") ?? readActiveId(),
  );
  const [showArchived, setShowArchived] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  /**
   * The agent this chat runs under.
   *
   * ONE piece of state, where there used to be two. An agent and a thinking
   * model were separate selections down separate paths — picking an agent
   * ignored the thinking model entirely — so the composer asked twice for one
   * decision and the answer could only ever honour one of them. Migration 0059
   * merged the records; this is the merge on the page.
   */
  const [agentId, setAgentId] = useState<string | null>(() => params.get("agent"));
  /**
   * The scope the NEXT question runs under.
   *
   * Held on the page rather than in the settings dialog because re-aiming the
   * next question is an ordinary mid-conversation act, unlike rewriting a
   * chat's standing instructions. Seeded from the user's resolved default and
   * from whatever the open chat was last asked under.
   */
  const [scope, setScope] = useState<KnowledgeScope>({ kind: "all" });
  /**
   * Documents pinned to the next turn.
   *
   * `name` is carried alongside the URI because the two sources of an
   * attachment name it differently: a pasted link has only its last path
   * segment to go on, while an uploaded file has the name OneDrive actually
   * stored — which `conflictBehavior=rename` may have changed. Only `uri` is
   * ever sent; the label is the browser's business.
   */
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState<{
    stage: CopilotChatActivityStage;
    message: string;
  } | null>(null);
  /**
   * The in-flight turn's abort handle, so the composer can stop waiting for it.
   *
   * A ref rather than state: the stop button reads it at click time, and a
   * re-render per controller would buy nothing — `busy` already says whether
   * there is a turn to stop.
   */
  const inFlight = useRef<AbortController | null>(null);
  const [reconnectMessage, setReconnectMessage] = useState<string | null>(null);
  /** The answer whose "Copied" tick is currently showing, if any. */
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const nextId = useRef(1);
  const historyRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** The chat whose stored transcript is already on screen. Guards against a
   *  late fetch replacing a live transcript with what the server kept — which,
   *  with retention off, is nothing. */
  const seededId = useRef<string | null>(null);
  const activeAnswerRender = useRef<{
    answerId: number;
    trace: CopilotLatencyTrace;
    lastText: string;
  } | null>(null);

  const appendTurn = useAppendCopilotTurn();
  const conversations = useCopilotConversations(showArchived);
  const conversation = useCopilotConversation(activeId);
  const settingsQuery = useCopilotSettings();
  const savedPromptsQuery = useCopilotSavedPrompts();
  const agentsQuery = useCopilotAgents();
  const updateConversation = useUpdateCopilotConversation();
  const deleteConversation = useDeleteCopilotConversation();
  const updateSettings = useUpdateCopilotSettings();
  const createSavedPrompt = useCreateCopilotSavedPrompt();
  const deleteSavedPrompt = useDeleteCopilotSavedPrompt();

  const settings: CopilotSettings | null = settingsQuery.data?.settings ?? null;
  /** Off until the tenant has consented the upload permission — see the API. */
  const uploadPolicy: CopilotUploadPolicy = settingsQuery.data?.uploads ??
    { enabled: false, maxBytes: 0 };
  const savedPrompts: CopilotSavedPrompt[] = savedPromptsQuery.data?.items ?? [];
  const agents: CopilotAgent[] = agentsQuery.data?.items ?? [];
  const active = conversation.data?.conversation ?? null;
  
  const activeAgent = agents.find((model) => model.id === agentId) ?? null;

  // Effects run after React commits the streamed snapshot to the visible tree.
  useEffect(() => {
    const pending = activeAnswerRender.current;
    if (!pending) return;
    const visible = messages.find(
      (message) => message.id === pending.answerId && message.role === "assistant",
    );
    if (!visible?.text || visible.text === pending.lastText) return;
    pending.trace.markOnce("first_answer_rendered");
    pending.trace.markOccurrence("answer_rendered");
    pending.lastText = visible.text;
  }, [messages]);

  // `replace` throughout: opening a chat is not a navigation the back button
  // should have to walk back through one conversation at a time.
  useEffect(() => {
    if (
      (params.get("chat") ?? null) === activeId &&
      (params.get("agent") ?? null) === agentId
    ) return;
    const next = new URLSearchParams(params);
    if (activeId) next.set("chat", activeId);
    else next.delete("chat");
    if (agentId) next.set("agent", agentId);
    else next.delete("agent");
    setParams(next, { replace: true });
    // `params` is deliberately absent: this effect reacts to the open chat
    // changing, and re-running it when the URL changes would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, agentId]);

  useEffect(() => {
    try {
      if (activeId) window.localStorage.setItem(ACTIVE_KEY, activeId);
      else window.localStorage.removeItem(ACTIVE_KEY);
    } catch {
      // A browser refusing storage costs the "resume where I was" convenience
      // and nothing else — the chat itself lives on the server.
    }
  }, [activeId]);

  // A chat that was deleted elsewhere (or on another device) must not leave the
  // page pointing at it.
  useEffect(() => {
    if (activeId && conversation.isError) {
      setActiveId(null);
      setMessages([]);
      seededId.current = null;
    }
  }, [activeId, conversation.isError]);

  // The scope follows the user's default until they touch the selector, and an
  // opened chat re-aims it to whatever that chat was last asked under — so
  // resuming a conversation resumes its aim, not the global default.
  useEffect(() => {
    if (settings?.knowledgeScope) setScope(settings.knowledgeScope);
  }, [settings?.knowledgeScope]);

  useEffect(() => {
    if (active?.knowledgeScope) setScope(active.knowledgeScope);
  }, [active?.id, active?.knowledgeScope]);

  useEffect(() => {
    if (active) setAgentId(active.agentId);
  }, [active?.id, active?.agentId]);

  // Load a stored transcript when a different chat is opened.
  useEffect(() => {
    if (!active || seededId.current === active.id) return;
    seededId.current = active.id;
    setMessages(
      active.messages.map((message) => ({
        id: nextId.current++,
        role: message.role,
        text: message.text,
        attributions: message.attributions,
        retrievedSources: message.retrievedSources,
        mcpSources: message.mcpSources,
        time: messageTime(message.createdAt),
      })),
    );
    setReconnectMessage(null);
  }, [active]);

  function scrollToLatest(): void {
    requestAnimationFrame(() => {
      historyRef.current?.lastElementChild?.scrollIntoView?.({
        behavior: "smooth",
        block: "end",
      });
    });
  }

  function newChat(): void {
    // No server round trip: the conversation row is created by the first turn,
    // so an abandoned "New chat" never reaches the session list at all.
    setActiveId(null);
    seededId.current = null;
    setMessages([]);
    setDraft("");
    setFiles([]);
    setFilePickerOpen(false);
    setReconnectMessage(null);
  }

  function openChat(id: string): void {
    if (id === activeId) return;
    setActiveId(id);
    setAgentId(null);
    setMessages([]);
    setFiles([]);
    setFilePickerOpen(false);
    // Clearing the seed marker is what makes the transcript load again. Leaving
    // it set is how reopening a chat used to come back blank, or showing only
    // the exchange that happened to be cached when it was first opened.
    seededId.current = null;
    setReconnectMessage(null);
  }

  /**
   * Choose the agent this chat runs under.
   *
   * One function, where selecting an agent and selecting a thinking model used
   * to be two that did incompatible things — the first started a whole new
   * conversation elsewhere, the second patched the open one. Selecting mid-chat
   * now patches the open chat, exactly as the thinking model did, because the
   * transcript is the same transcript either way.
   */
  function selectAgent(id: string | null): void {
    if (id === agentId) return;
    setAgentId(id);
    // An agent locked to a Microsoft 365 source re-aims the composer with it,
    // so the scope chip says what the next question will actually search.
    const chosen = agents.find((item) => item.id === id);
    if (chosen?.knowledgeScope) setScope(chosen.knowledgeScope);
    setReconnectMessage(null);
    if (activeId) {
      patchConversation(activeId, { agentId: id }, "Could not change the agent.");
    }
  }

  // `submitAgentMessage` stood here and sent the turn to /agents/:id/chat
  // instead — a separate transcript, a separate provider, and no sight of the
  // user's Microsoft 365. There is one path now: an agent's instructions, its
  // knowledge and its tool results are folded into the ordinary Copilot turn
  // server-side, so a selected agent answers from everything it holds AND from
  // the mail, meetings and files the person can already open.

  async function submitMessage(
    rawMessage: string,
    aimedAt?: KnowledgeScope,
  ): Promise<void> {
    const message = rawMessage.trim();
    if (!message || busy) return;

    // An override beats the selector for THIS turn only: the "ask all of
    // Microsoft 365 anyway" button must not silently re-aim the chat, and
    // reading `scope` here would read the value from before `setScope`.
    const turnScope = aimedAt ?? scope;

    const controller = new AbortController();
    inFlight.current = controller;
    const answerId = nextId.current++;
    const latency = new CopilotLatencyTrace();
    latency.mark("user_submit");
    activeAnswerRender.current = { answerId, trace: latency, lastText: "" };

    setDraft("");
    setBusy(true);
    setActivity({ stage: "preparing", message: "Preparing your request…" });
    setReconnectMessage(null);
    setMessages((current) => [
      ...current,
      { id: nextId.current++, role: "user", text: message, time: messageTime() },
    ]);
    scrollToLatest();

    try {
      const result = await sendCopilotMessage({
        message,
        conversationId: activeId ?? undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        knowledgeScope: turnScope,
        agentId,
        // URIs only. The chip label is a browser-side convenience and has no
        // business travelling to Microsoft as part of the request.
        files: files.length > 0 ? files.map(({ uri }) => ({ uri })) : undefined,
        // Source selection is part of the turn, not merely a standing consent.
        // The server combines this selection with the user's saved MCP source
        // mode. In MCP-only mode it is a hard boundary; otherwise the results
        // supplement the permitted Microsoft 365 sources.
        mcpIntegrationIds: businessTools.isFetched
          ? businessTools.activeItems.map((item) => item.id)
          : undefined,
        // No `mcpContextIds`. Business tools are no longer something the
        // browser supplies evidence for: it names the selected integrations,
        // while the server owns credentials, consent, discovery, execution and
        // reports only what it actually used in `mcpSources`.
      }, controller.signal, (event) => {
        if (event.type === "status") {
          setActivity({ stage: event.stage, message: event.message });
          scrollToLatest();
          return;
        }
        if (!event.answer.trim()) return;
        setMessages((current) => {
          const present = current.some((item) => item.id === answerId);
          if (!present) {
            return [
              ...current,
              {
                id: answerId,
                role: "assistant",
                text: event.answer,
                streaming: true,
                time: messageTime(),
              },
            ];
          }
          return current.map((item) =>
            item.id === answerId
              ? { ...item, text: event.answer, streaming: true }
              : item,
          );
        });
        scrollToLatest();
      }, latency);
      // First turn of a new chat: adopt the id the server minted, and claim it
      // as seeded so the detail fetch does not wipe what is on screen.
      if (!activeId) {
        seededId.current = result.conversationId;
        setActiveId(result.conversationId);
      }
      setMessages((current) => {
        const finalMessage: ChatMessage = {
          id: answerId,
          role: "assistant",
          text: result.answer,
          streaming: false,
          attributions: result.attributions,
          retrievedSources: result.retrievedSources,
          mcpSources: result.mcpSources,
          time: current.find((item) => item.id === answerId)?.time ?? messageTime(),
        };
        return current.some((item) => item.id === answerId)
          ? current.map((item) => (item.id === answerId ? finalMessage : item))
          : [...current, finalMessage];
      });
      // Keep the cached transcript complete, so leaving this chat and coming
      // back shows every turn rather than the state it was first fetched in.
      appendTurn({
        conversationId: result.conversationId,
        prompt: message,
        answer: result.answer,
        attributions: result.attributions,
        retrievedSources: result.retrievedSources,
        mcpSources: result.mcpSources,
      });
      setFiles([]);
      setFilePickerOpen(false);
      void conversations.refetch();
    } catch (error) {
      // Stopping is not a failure: the user asked to stop waiting, so the turn
      // is closed with a note instead of an error, and nothing about the
      // Microsoft sign-in is implicated.
      if (controller.signal.aborted) {
        setMessages((current) =>
          current.map((item) =>
            item.id === answerId ? { ...item, streaming: false } : item,
          ),
        );
        setMessages((current) => [
          ...current,
          {
            id: nextId.current++,
            role: "stopped",
            text: "Stopped. Ask again to get the answer.",
            time: messageTime(),
          },
        ]);
        return;
      }
      // The API already words the problem for the specific code, so its message
      // is shown rather than one fixed sentence for every cause.
      setReconnectMessage(
        error instanceof CopilotApiError && RECONNECTABLE.has(error.code)
          ? error.message ||
            "Copilot permissions are missing from your current sign-in."
          : null,
      );
      setMessages((current) =>
        current.map((item) =>
          item.id === answerId ? { ...item, streaming: false } : item,
        ),
      );
      setMessages((current) => [
        ...current,
        {
          id: nextId.current++,
          role: "error",
          text: error instanceof Error ? error.message : "Copilot request failed.",
          // Nothing matched where the chat was aimed: the one failure with an
          // obvious next move, offered as a button rather than an explanation.
          retryUnscoped:
            error instanceof CopilotApiError && error.code === "COPILOT_SCOPE_EMPTY"
              ? message
              : undefined,
          // Everything else that is not a permissions problem is worth one
          // more attempt, unchanged — a timeout or a 5xx upstream usually is.
          // A missing grant is not: retrying it walks the same wall again, and
          // the reconnect banner above is the actual next move.
          retryPrompt:
            error instanceof CopilotApiError &&
            (RECONNECTABLE.has(error.code) ||
              error.code === "COPILOT_SCOPE_EMPTY" ||
              error.code === "COPILOT_ACCESS_DENIED")
              ? undefined
              : message,
          time: messageTime(),
        },
      ]);
    } finally {
      // Only if this turn is still the current one: a stop followed by a new
      // question must not have its controller cleared by the old turn unwinding.
      if (inFlight.current === controller) inFlight.current = null;
      setBusy(false);
      setActivity(null);
      scrollToLatest();
    }
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await submitMessage(draft);
  }

  /** Stops waiting for the turn in flight, if there is one. */
  function stopMessage(): void {
    inFlight.current?.abort();
  }

  /**
   * The question an answer belongs to: the nearest user turn before it.
   *
   * Derived rather than stored on the message. The transcript is already the
   * record of what was asked, and a copy of the prompt on every answer is a
   * second copy to keep true through streaming, stored-transcript seeding and
   * "ask all of Microsoft 365" retries.
   */
  function promptBefore(messageId: number): string | null {
    const index = messages.findIndex((item) => item.id === messageId);
    if (index < 0) return null;
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      const candidate = messages[cursor];
      if (candidate?.role === "user") return candidate.text;
    }
    return null;
  }

  /**
   * Copy an answer to the clipboard.
   *
   * `navigator.clipboard` is unavailable on an insecure origin and can be
   * refused by permission policy, so the failure is reported rather than
   * swallowed — a copy button that silently does nothing is worse than none.
   */
  async function copyAnswer(message: ChatMessage): Promise<void> {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopiedId(message.id);
      window.setTimeout(
        () => setCopiedId((current) => (current === message.id ? null : current)),
        1_500,
      );
    } catch {
      toast.error("Your browser would not let the answer be copied.");
    }
  }

  /**
   * Put a question back in the composer to be reworded.
   *
   * Deliberately NOT an edit of the transcript: the turn was asked, Copilot
   * answered it, and the upstream conversation holds both. Rewriting what is on
   * screen would make the history disagree with the answer below it. So this
   * loads the text and leaves the exchange standing.
   */
  function editQuestion(text: string): void {
    setDraft(text);
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      textarea?.focus();
      textarea?.setSelectionRange(text.length, text.length);
    });
  }

  const exportable = useMemo(
    () =>
      messages.map((message) => ({
        role: message.role,
        text: message.text,
        attributions: message.attributions,
      })),
    [messages],
  );

  /**
   * Exports what is on screen rather than refetching the stored transcript:
   * that is the same thing for a saved chat, and the only thing there is for a
   * user who keeps no transcripts.
   */
  function exportChat(format: "md" | "json"): void {
    const now = new Date().toISOString();
    const target: CopilotConversationSummary =
      active ?? {
        id: "unsaved",
        title: "New chat",
        titleIsCustom: false,
        isPinned: false,
        isArchived: false,
        turnCount: Math.ceil(messages.length / 2),
        graphConversationId: null,
        api: null,
        agentId,
        lastActivityAt: now,
        createdAt: now,
      };
    const name = exportFileName(target.title);
    // Where the chat is aimed is part of the transcript: an answer of "nothing
    // was found" cannot be judged without knowing where it was looked for.
    // The saved chat's own scope wins over the selector, which may already have
    // been re-aimed for a question that has not been asked yet.
    const aimed = active?.knowledgeScope ?? scope;
    const exportScope = { label: scopeLabel(aimed), scope: aimed };
    if (format === "md") {
      downloadFile(`${name}.md`, "text/markdown", toMarkdown(target, exportable, exportScope));
    } else {
      downloadFile(`${name}.json`, "application/json", toJson(target, exportable, exportScope));
    }
  }

  /**
   * Copy a link that reopens this chat.
   *
   * Nothing is minted: the address bar already carries `?chat=<id>`, so this
   * copies the page's own URL. A chat that has not been asked anything yet has
   * no id and therefore no link, and saying so is better than copying a link
   * to "a new chat" that lands anyone who follows it somewhere else.
   */
  async function copyChatLink(): Promise<void> {
    if (!activeId) {
      toast.info("This chat has no link until you ask something.");
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set("chat", activeId);
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Your browser would not let the link be copied.");
    }
  }

  function patchConversation(
    id: string,
    input: Parameters<typeof updateConversation.mutate>[0]["input"],
    failure: string,
  ): void {
    updateConversation.mutate(
      { id, input },
      { onError: (error) => toast.error(error instanceof Error ? error.message : failure) },
    );
  }

  async function removeConversation(target: CopilotConversationSummary): Promise<void> {
    const ok = await confirm({
      title: `Delete “${target.title}”?`,
      body: "This cannot be undone.",
      confirmLabel: "Delete chat",
      variant: "danger",
    });
    if (!ok) return;
    deleteConversation.mutate(target.id, {
      onSuccess: () => {
        if (target.id === activeId) newChat();
        toast.success("Chat deleted.");
      },
      onError: (error) =>
        toast.error(error instanceof Error ? error.message : "Could not delete the chat.", {
          // The canvas's error toast carries its one recovery. Deleting was
          // already confirmed, so retrying does not ask again.
          action: { label: "Retry", onAction: () => deleteConversation.mutate(target.id) },
        }),
    });
  }

  function saveDefaults(input: Partial<CopilotSettings>): void {
    updateSettings.mutate(input, {
      onSuccess: () => {
        toast.success("Settings saved.");
        setSettingsOpen(false);
      },
      onError: (error) =>
        toast.error(error instanceof Error ? error.message : "Could not save settings."),
    });
  }

  function saveConversationProfile(input: Partial<CopilotInstructionProfile>): void {
    if (!activeId) return;
    updateConversation.mutate(
      { id: activeId, input },
      {
        onSuccess: () => {
          toast.success("Settings applied to this chat.");
          setSettingsOpen(false);
        },
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : "Could not save settings."),
      },
    );
  }

  /**
   * The response-shaping half of the profile — what the settings dialog edits.
   *
   * `knowledgeScope` is excluded because it is NOT edited there: it lives on
   * the selector beside the composer, and a conversation carries `null` for it
   * until a turn lands. Typing this as the full profile would force the page to
   * invent a scope for a chat that has not been asked anything yet.
   */
  const profile: Omit<CopilotInstructionProfile, "knowledgeScope"> | null =
    active ?? settings;

  /**
   * Flip web grounding for the chat you are in, or — with no chat open yet —
   * for the default the next one will inherit. The canvas puts this switch on
   * the composer, so it has to write through the same profile the settings
   * dialog edits rather than keeping a second copy of the flag.
   */
  function toggleWeb(): void {
    const next = !(profile?.webGrounding ?? false);
    if (activeId) {
      patchConversation(activeId, { webGrounding: next }, "Could not change web search.");
    } else {
      updateSettings.mutate(
        { webGrounding: next },
        {
          onError: (error) =>
            toast.error(error instanceof Error ? error.message : "Could not change web search."),
        },
      );
    }
  }

  function setResponseLength(responseLength: CopilotResponseLength): void {
    if (activeId) {
      patchConversation(activeId, { responseLength }, "Could not change the answer length.");
    } else {
      updateSettings.mutate(
        { responseLength },
        {
          onError: (error) =>
            toast.error(
              error instanceof Error ? error.message : "Could not change the answer length.",
            ),
        },
      );
    }
  }

  return (
    <div className="cp-page">
      <NavChatSlot>
        <CopilotSessionList
          conversations={conversations.data?.items ?? []}
          activeId={activeId}
          isLoading={conversations.isLoading}
          showArchived={showArchived}
          onSelect={openChat}
          onNewChat={newChat}
          onRename={(id, title) => patchConversation(id, { title }, "Could not rename the chat.")}
          onTogglePin={(target) =>
            patchConversation(target.id, { isPinned: !target.isPinned }, "Could not pin the chat.")
          }
          onToggleArchive={(target) =>
            patchConversation(
              target.id,
              { isArchived: !target.isArchived },
              "Could not archive the chat.",
            )
          }
          onDelete={removeConversation}
          onSetShowArchived={setShowArchived}
        />
      </NavChatSlot>

      <ConversationView
        active={active}
        messages={messages}
        busy={busy}
        activity={activity}
        savedPrompts={savedPrompts}
        copiedId={copiedId}
        reconnectMessage={reconnectMessage}
        profile={profile}
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={(event) => void onSubmit(event)}
        onStop={stopMessage}
        onAsk={(message, aimedAt) => void submitMessage(message, aimedAt)}
        onCopyAnswer={(message) => void copyAnswer(message)}
        onEditQuestion={editQuestion}
        promptBefore={promptBefore}
        files={files}
        onFilesChange={setFiles}
        uploadPolicy={uploadPolicy}
        filePickerOpen={filePickerOpen}
        onFilePickerOpenChange={setFilePickerOpen}
        onReconnectRequired={setReconnectMessage}
        scope={scope}
        onScopeChange={setScope}
        agentId={agentId}
        agents={agents}
        activeAgent={activeAgent}
        onAgentChange={selectAgent}
        onOpenSettings={() => setSettingsOpen(true)}
        onToggleWeb={toggleWeb}
        onResponseLengthChange={setResponseLength}
        onExport={exportChat}
        onCopyLink={() => void copyChatLink()}
        composerRef={composerRef}
        textareaRef={textareaRef}
        historyRef={historyRef}
      />

      {settingsOpen && settings && (
        <CopilotSettingsDialog
          settings={settings}
          conversation={active}
          savedPrompts={savedPrompts}
          busy={updateSettings.isPending || updateConversation.isPending}
          onClose={() => setSettingsOpen(false)}
          onSaveDefaults={saveDefaults}
          onSaveConversation={saveConversationProfile}
          onCreateSavedPrompt={(input) =>
            createSavedPrompt.mutate(input, {
              onSuccess: () => toast.success(`Prompt “${input.name}” saved.`),
              onError: (error) =>
                toast.error(
                  error instanceof Error ? error.message : "Could not save the prompt.",
                ),
            })
          }
          onDeleteSavedPrompt={(savedPrompt) =>
            deleteSavedPrompt.mutate(savedPrompt.id, {
              onError: (error) =>
                toast.error(
                  error instanceof Error ? error.message : "Could not delete the prompt.",
                ),
            })
          }
        />
      )}
    </div>
  );
}
